import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, Antenna, Radio, Network, CheckCircle } from 'lucide-react'; // Removed Lightbulb icon as it's no longer needed

// Mock Icon Component (replace with your actual AppIcon if available)
const iconMap = {
  Cpu,
  Antenna,
  Radio,
  Network,
  CheckCircle,
};

const Icon = ({ name, size = 24, className = '' }) => {
  const LucideIcon = iconMap[name];
  if (!LucideIcon) {
    // Fallback or error handling for missing icons
    console.warn(`Icon "${name}" not found.`);
    return <div className={`w-${size} h-${size} bg-gray-300 rounded-full ${className}`}></div>;
  }
  return <LucideIcon size={size} className={className} />;
};

// Mock CleanCard Component (replace with your actual FuturisticCard if available)
const CleanCard = ({ children, variant = 'default', className = '', ...props }) => {
  const baseClasses = 'rounded-xl border transition-all duration-200 relative overflow-hidden'; // Added relative and overflow-hidden
  const variantClasses = {
    default: 'bg-white border-gray-200 text-gray-800',
    primary: 'bg-gradient-to-br from-brand-primary/10 to-brand-primary/5 border-brand-primary text-brand-primary',
  };

  return (
    <motion.div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      {...props}
    >
      {/* Animated Border Element */}
      <div className="absolute inset-0 rounded-xl pointer-events-none z-10"
        style={{
          background: variant === 'primary' ? 'linear-gradient(90deg, #007bff 0%, #a855f7 50%, #007bff 100%)' : 'none',
          backgroundSize: '200% 100%',
          animation: variant === 'primary' ? 'border-pulse 2s linear infinite' : 'none',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
          WebkitMaskComposite: 'exclude', // For Safari
          padding: '2px', // This creates the visible border effect
        }}
      ></div>
      <div className="relative z-20 h-full flex flex-col"> {/* Content wrapper to keep content above border */}
        {children}
      </div>
    </motion.div>
  );
};

// Mock CleanHeading Component (replace with your actual HolographicText if available)
const CleanHeading = ({ children, size = 'text-3xl', variant = 'default', className = '' }) => {
  const baseClasses = 'font-bold font-heading';
  const variantClasses = {
    default: 'text-gray-900',
    primary: 'text-telecom-primary',
  };

  return (
    <h2 className={`${baseClasses} ${variantClasses[variant]} ${size} ${className}`}>
      {children}
    </h2>
  );
};

const CapabilityMatrix = () => {
  const [activeCapability, setActiveCapability] = useState(null);
  // Removed insightLoading and generatedInsights states as the feature is removed
  // const [insightLoading, setInsightLoading] = useState(false);
  // const [generatedInsights, setGeneratedInsights] = useState({});

  const capabilities = [
    {
      id: 'components',
      title: 'Telecom Components',
      icon: 'Cpu',
      description: 'Precision-engineered components for network infrastructure',
      outcomes: ['99.9% Component Reliability', '50% Faster Signal Processing', '30% Reduced Power Consumption'],
      clients: ['Network Equipment Manufacturers', 'Telecom Operators', 'Infrastructure Providers']
    },
    {
      id: 'infrastructure',
      title: 'Network Infrastructure',
      icon: 'Antenna',
      description: 'Complete network solutions and connectivity systems',
      outcomes: ['99.99% Network Uptime', '40% Improved Coverage', '25% Reduced Latency'],
      clients: ['Mobile Network Operators', 'ISPs', 'Enterprise Networks']
    },
    {
      id: 'transmission',
      title: 'Signal Transmission',
      icon: 'Radio',
      description: 'Advanced signal processing and transmission technologies',
      outcomes: ['10x Signal Clarity', '60% Bandwidth Efficiency', '35% Range Extension'],
      clients: ['Broadcast Companies', 'Satellite Operators', 'Wireless Providers']
    },
    {
      id: 'integration',
      title: 'System Integration',
      icon: 'Network',
      description: 'Seamless integration of telecommunications systems',
      outcomes: ['100% System Compatibility', '70% Faster Deployment', '90% Integration Success'],
      clients: ['System Integrators', 'Technology Partners', 'Enterprise Clients']
    }
  ];

  // Removed generateCapabilityInsight function as the feature is removed
  // const generateCapabilityInsight = async (capability) => {
  //   setInsightLoading(true);
  //   try {
  //     const prompt = `Generate a concise, impactful paragraph (around 80-120 words) providing a strategic insight or future outlook for "${capability.title}" in the telecommunications industry. Consider its description: "${capability.description}", key outcomes: "${capability.outcomes.join(', ')}", and typical clients: "${capability.clients.join(', ')}". Focus on its importance, emerging trends, or potential impact.`;

  //     let chatHistory = [];
  //     chatHistory.push({ role: "user", parts: [{ text: prompt }] });

  //     const payload = { contents: chatHistory };
  //     const apiKey = ""; // Canvas will automatically provide the API key at runtime
  //     const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

  //     const response = await fetch(apiUrl, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(payload)
  //     });

  //     const result = await response.json();

  //     if (result.candidates && result.candidates.length > 0 &&
  //         result.candidates[0].content && result.candidates[0].content.parts &&
  //         result.candidates[0].content.parts.length > 0) {
  //       const text = result.candidates[0].content.parts[0].text;
  //       setGeneratedInsights(prev => ({ ...prev, [capability.id]: text }));
  //     } else {
  //       console.error("Gemini API response structure unexpected:", result);
  //       setGeneratedInsights(prev => ({ ...prev, [capability.id]: "Failed to generate insight. Please try again." }));
  //     }
  //   } catch (error) {
  //     console.error("Error calling Gemini API:", error);
  //     setGeneratedInsights(prev => ({ ...prev, [capability.id]: "Error generating insight. Check console for details." }));
  //   } finally {
  //     setInsightLoading(false);
  //   }
  // };

  return (
    // Tailwind CSS and Font imports for self-contained example
    <>
      <script src="https://cdn.tailwindcss.com"></script>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet" />
      <style>
        {`
          body {
            font-family: 'Inter', sans-serif;
          }
          /* Custom Tailwind colors for demonstration */
          .text-telecom-primary { color: #007bff; }
          .text-telecom-secondary { color: #6c757d; }
          .text-telecom-accent { color: #28a745; }
          .text-foreground { color: #333333; }
          .text-text-secondary { color: #666666; }
          .text-text-muted { color: #999999; }
          .border-border { border-color: #e2e8f0; }
          .bg-brand-primary\\/10 { background-color: rgba(0, 123, 255, 0.1); }
          .bg-brand-primary\\/5 { background-color: rgba(0, 123, 255, 0.05); }
          .border-brand-primary { border-color: #007bff; }
          .border-brand-primary\\/30 { border-color: rgba(0, 123, 255, 0.3); }
          .bg-telecom-secondary\\/10 { background-color: rgba(108, 117, 125, 0.1); }

          /* Keyframe animation for the border pulse */
          @keyframes border-pulse {
            0% { background-position: 0% 0%; }
            100% { background-position: -200% 0%; }
          }
        `}
      </style>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <CleanHeading
              size="text-3xl sm:text-4xl lg:text-5xl"
              variant="primary"
              className="mb-6"
            >
              Our Core Capabilities
            </CleanHeading>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Over 10 years of specialized expertise in telecommunications components and network infrastructure,
              delivering <span className="text-telecom-primary font-semibold">precision-engineered solutions</span> that power global connectivity.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                layout // Enables smooth layout transitions for the card itself
              >
                <CleanCard
                  variant={activeCapability === capability.id ? 'primary' : 'default'}
                  className={`p-6 cursor-pointer h-full flex flex-col ${
                    activeCapability === capability.id
                      ? 'shadow-lg border-brand-primary/30'
                      : 'hover:shadow-md'
                  }`}
                  onMouseEnter={() => setActiveCapability(capability.id)}
                  onMouseLeave={() => setActiveCapability(null)}
                >
                  {/* Icon */}
                  <div
                    className={`w-16 h-16 rounded-lg flex items-center justify-center mb-6 transition-all duration-300 ${
                      activeCapability === capability.id
                        ? 'bg-brand-primary/10 text-brand-primary'
                        : 'bg-telecom-secondary/10 text-telecom-secondary'
                    }`}
                  >
                    <Icon name={capability.icon} size={28} />
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-bold text-foreground font-heading mb-3">
                    {capability.title}
                  </h3>
                  <p className="text-text-secondary font-body mb-6 flex-grow">
                    {capability.description}
                  </p>

                  {/* Outcomes & Clients - Animated with Framer Motion */}
                  <AnimatePresence>
                    {activeCapability === capability.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden" // Essential to clip content during height animation
                      >
                        <div className="space-y-4 pt-4 border-t border-border">
                          <div>
                            <h4 className="text-sm font-semibold text-brand-primary mb-2">Key Outcomes:</h4>
                            <ul className="space-y-2">
                              {capability.outcomes.map((outcome, idx) => (
                                <li
                                  key={idx}
                                  className="text-sm text-text-secondary flex items-start"
                                >
                                  <Icon name="CheckCircle" size={16} className="text-telecom-secondary mr-2 mt-0.5 flex-shrink-0" />
                                  <span>{outcome}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <h4 className="text-sm font-semibold text-telecom-accent mb-2">Recent Clients:</h4>
                            <ul className="space-y-1">
                              {capability.clients.map((client, idx) => (
                                <li
                                  key={idx}
                                  className="text-sm text-text-muted"
                                >
                                  {client}
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Removed the Gemini API Insight Generator section */}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CleanCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default CapabilityMatrix;
